/** Client API centralisé avec gestion du token JWT */

import type { EmployeeActivity } from '../types';

const API_URL = import.meta.env.VITE_API_URL;

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/** Wrapper fetch avec gestion automatique du 401 */
async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...options?.headers },
  });

  // Token expiré ou invalide → déconnexion
  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Non autorisé');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `Erreur API: ${response.status}`);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/** Convertit un objet de paramètres en query string */
function toQueryString(params: Record<string, unknown>): string {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (filtered.length === 0) return '';
  return '?' + new URLSearchParams(
    filtered.map(([k, v]) => [k, String(v)])
  ).toString();
}

// --- Auth ---
export const authApi = {
  login: (username: string, password: string) =>
    fetchApi<{ challenge_id: string; email_hint: string; expires_in_seconds: number }>(
      '/api/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }
    ),

  verify: (challenge_id: string, code: string) =>
    fetchApi<{ access_token: string }>('/api/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, code }),
    }),

  me: () =>
    fetchApi<{
      username: string;
      email: string | null;
      role: 'admin' | 'manager';
      // Équipe gérée (chefs d'équipe uniquement, null = non restreint)
      activity: EmployeeActivity | null;
      must_change_password: boolean;
    }>('/api/auth/me'),

  changePassword: (current_password: string, new_password: string) =>
    fetchApi<void>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  requestPasswordReset: (email: string) =>
    fetchApi<void>('/api/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  confirmPasswordReset: (token: string, new_password: string) =>
    fetchApi<void>('/api/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    }),
};

// --- Employees ---
import type {
  Employee,
  EmployeeCreate,
  EmployeeUpdate,
  PaginatedResponse,
} from '../types';

export const employeesApi = {
  list: (params: { page?: number; per_page?: number; search?: string; active_only?: boolean; site_id?: number }) =>
    fetchApi<PaginatedResponse<Employee>>(`/api/employees${toQueryString(params)}`),

  get: (id: number) =>
    fetchApi<Employee>(`/api/employees/${id}`),

  create: (data: EmployeeCreate) =>
    fetchApi<Employee>('/api/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: EmployeeUpdate) =>
    fetchApi<Employee>(`/api/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<void>(`/api/employees/${id}`, { method: 'DELETE' }),

  assignBadge: (id: number, badge_uid: string) =>
    fetchApi<Employee>(`/api/employees/${id}/badge`, {
      method: 'POST',
      body: JSON.stringify({ badge_uid }),
    }),

  removeBadge: (id: number) =>
    fetchApi<Employee>(`/api/employees/${id}/badge`, { method: 'DELETE' }),
};

// --- Time Logs ---
import type { TimeLog, DailySummaryEmployee, WeeklySummaryResponse } from '../types';

export const timeLogsApi = {
  list: (params: {
    page?: number; per_page?: number; employee_id?: number;
    date_from?: string; date_to?: string; type?: string; reader_id?: number;
    activity?: string;
  }) =>
    fetchApi<PaginatedResponse<TimeLog>>(`/api/time-logs${toQueryString(params)}`),

  dailySummary: (date: string) =>
    fetchApi<{ date: string; employees: DailySummaryEmployee[] }>(
      `/api/time-logs/daily-summary?date=${date}`
    ),

  summary: (employee_id: number, date_from: string, date_to: string) =>
    fetchApi<WeeklySummaryResponse>(
      `/api/time-logs/summary?employee_id=${employee_id}&date_from=${date_from}&date_to=${date_to}`
    ),

  exportUrl: (params: { date_from?: string; date_to?: string; employee_id?: number }) =>
    `${API_URL}/api/time-logs/export${toQueryString(params)}`,
};

// --- Readers ---
import type { Reader, ReaderCreate } from '../types';

export const readersApi = {
  list: (site_id?: number) =>
    fetchApi<Reader[]>(`/api/readers${toQueryString({ site_id })}`),

  create: (data: ReaderCreate) =>
    fetchApi<Reader>('/api/readers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<ReaderCreate>) =>
    fetchApi<Reader>(`/api/readers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<void>(`/api/readers/${id}`, { method: 'DELETE' }),

  createPairingCode: (id: number) =>
    fetchApi<{ pin: string; expires_at: string }>(`/api/readers/${id}/pairing`, {
      method: 'POST',
    }),

  revokeDevice: (id: number) =>
    fetchApi<void>(`/api/readers/${id}/device-token`, { method: 'DELETE' }),
};

// --- Dashboard ---
import type { DashboardStats, PresenceData } from '../types';

export interface DepartmentStat {
  name: string;
  site_name: string | null;
  present: number;
  total: number;
  rate: number;
}

export interface ByDepartmentData {
  timestamp: string;
  departments: DepartmentStat[];
}

export const dashboardApi = {
  stats: (site_id?: number) =>
    fetchApi<DashboardStats>(`/api/dashboard/stats${toQueryString({ site_id })}`),

  presence: (site_id?: number) =>
    fetchApi<PresenceData>(`/api/dashboard/presence${toQueryString({ site_id })}`),

  byDepartment: (site_id?: number) =>
    fetchApi<ByDepartmentData>(`/api/dashboard/by-department${toQueryString({ site_id })}`),
};
